import React, { useEffect, useMemo, useState } from 'react';
import DataTable from '../../../../../../common/components/DataTable';
import { IDataTableColumn } from '../../../../../../common/types/dataTable';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../../../template/store';
import { setLinerAttachment } from '../../../instructionSlice/DOExtensionSlice';
import UploadThing from '../../../../../../../components/common/UploadThing';
import Select from 'react-select';
import { updateSelectedCfsName, updateSelectedemptyYardName } from '../../../instructionSlice/DOExtensionSlice';
import useBlCountry from '../../../../../../../hooks/useBlCountry';
import { useLinerConfig } from '../../../../../../../hooks/useLinerConfig';
import { getReleaseInstructionCountryConfig } from '../../config';
import useDataCountry from '../../../../../../../hooks/useDataCountry';
import { encryptAndEncode } from '../../../../../../../encrypt-util';
import { openDocumentFromUrl, openDocumentInNewTab } from '../../../../../../../helpers/openDocumentInNewTab';

interface LinerReleaseAttachmentTableProps {
  accessGroupLevel: string[];
  attachedFileSize: number;
  approveRejectAccess: boolean;
  releaseInstructionConfig: ReturnType<typeof getReleaseInstructionCountryConfig>;
  blCountry: string;
  disableEmptyYard?: boolean;
}

const LinerReleaseAttachmentTable: React.FC<LinerReleaseAttachmentTableProps> = ({
  accessGroupLevel,
  attachedFileSize,
  approveRejectAccess,
  releaseInstructionConfig,
  blCountry,
  disableEmptyYard = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const linerDocs = useSelector((state: RootState) => state.doExtension.linerDocs);
  const profileDetails = useSelector((state: RootState) => state.userProfile.profile);
  const demurrageViewForm = useSelector(
    (state: RootState) => state.doExtension.demurrageFormView
  );
  const demurrageForm = useSelector((state: RootState) => state.doExtension.demurrageFormView);
  const instructionList = useSelector((state: RootState) => state.documentHistory.data);
  const selectedLinerId = useSelector((state: RootState) => state.navbarTop.linerId);
  // const dataCountry = useBlCountry('view', 'import', 804)[0];
  // const blCountry = useBlCountry('view', 'import', 804)[0];
  const dataCountry = useDataCountry();
  const rbac = useSelector((state: RootState) => state.userProfile);
  const csfList = useSelector((state: RootState) => state.doExtension.cfsList);
  const emptyYardList = useSelector((state: RootState) => state.doExtension.emptyYardList);
  const selectedCfsName = useSelector((state: RootState) => state.doExtension.selectedCfsName);
  const selectedEmptyYardName = useSelector(
    (state: RootState) => state.doExtension.selectedemptyYardName
  );
  // const linerConfig = useLinerConfig(blCountry, selectedLinerId, 804);
 const [pilmzUploadedDocs, setPilmzUploadedDocs] = useState<
    Record<number, { fileName: string; serverPath: string; tempName: string }[]>
  >({}); // PIL MZ - New Feature: Multi document upload

  useEffect(() => {
    if (!releaseInstructionConfig?.fields?.autoPopulateCFSAndEmptyYard) return;

    // PIL MZ - New Feature: Auto-populate CFS and Empty Yard
    if (!selectedCfsName?.value && demurrageViewForm?.cfsName && csfList?.length) {
      const cfsMatch = csfList.find((x: any) => x.value == demurrageViewForm.cfsName);
      if (cfsMatch) {
        dispatch(
          updateSelectedCfsName({
            label: cfsMatch.text,
            value: cfsMatch.value,
          })
        );
      }
    }

    if (!selectedEmptyYardName?.value && !disableEmptyYard && demurrageViewForm?.emptyYardName && emptyYardList?.length) {
      const emptyMatch = emptyYardList.find(
        (x: any) => x.value == demurrageViewForm.emptyYardName
      );
      if (emptyMatch) {
        dispatch(
          updateSelectedemptyYardName({
            label: emptyMatch.text,
            value: emptyMatch.value,
          })
        );
      }
    }
  }, [
    releaseInstructionConfig?.fields?.autoPopulateCFSAndEmptyYard,
    demurrageViewForm?.cfsName,
    demurrageViewForm?.emptyYardName,
    csfList,
    emptyYardList,
    selectedCfsName?.value,
    selectedEmptyYardName?.value,
    disableEmptyYard,
    dispatch,
  ]);

  const isViewOnly = useMemo(() => {
    return (
      instructionList
        .filter((v: any) => v?.docId === demurrageViewForm?.dndInstructionId)
        .map((u: any) => u?.event === 'Completed')
        .includes(true) ||
      (demurrageForm.status == 'Completed' ||
      demurrageForm.status?.includes('Rejected')) ||
      !(
        accessGroupLevel[demurrageViewForm.groupLevel] == 'Y' ||
        !accessGroupLevel.includes('Y')
      ) ||
      dataCountry !== blCountry ||
      rbac.rbacDetails.doExtApproveReject !== 'Y'
    );
  }, [
    instructionList,
    demurrageViewForm?.dndInstructionId,
    demurrageForm.status,
    accessGroupLevel,
    demurrageViewForm.groupLevel,
    dataCountry,
    blCountry,
    rbac.rbacDetails.doExtApproveReject,
  ]);

  const TableCellCfsList = (rowData: any) => {
    const { docId } = rowData.row.original;
    if (docId != 206 && docId != 208) return <></>;

    if (isViewOnly) {
      return (
        <span>
          {docId != 206
            ? selectedEmptyYardName?.label ??
              emptyYardList.find((x) => x.value == selectedEmptyYardName.value)?.text ??
              ''
            : selectedCfsName?.label ??
              csfList.find((x) => x.value == selectedCfsName.value)?.text ??
              ''}
        </span>
      );
    }

    return (
      <div style={{ zIndex: 999 }}>
        <Select
          menuPortalTarget={document.body}
          placeholder={docId == 206 ? 'CFS Name' : 'Empty Yard Name '}
          options={
            docId == 206
              ? csfList?.map((x) => ({ label: x?.text, value: x?.value }))
              : emptyYardList?.map((x) => ({ label: x?.text, value: x?.value }))
          }
          value={docId == 206 ? selectedCfsName : selectedEmptyYardName}
          classNames={{
            control: (state) => {
              return `dropControl asyncDropField m-0 p-0
              form-control ${state.isDisabled ? 'disabledField' : ''}
              ${state.isFocused ? 'focusField' : ''}`;
            },
          }}
          styles={{
            menuPortal: (base) => ({ ...base, fontSize: '12px', zIndex: 9999 }),
            control: (base) => ({ fontSize: '12px' }),
          }}
          onChange={(e: any) => {
            if (e == null) {
              e = {
                label: null,
                value: 0,
              };
            }
            dispatch(
              docId == 206 ? updateSelectedCfsName(e) : updateSelectedemptyYardName(e)
            );
          }}
          isDisabled={isViewOnly || (docId == 208 && disableEmptyYard)}
          isClearable={true}
        />
      </div>
    );
  };

  const linerDocAttachmentColumns: IDataTableColumn[] = useMemo(
    () => [
      {
        accessor: 'dndInstructionAttId',
        Header: 'dndInstructionAttId',
      },
      {
        accessor: 'docId',
        Header: 'Document Name',
        Cell: (rowData: any) => {
          const { docId, name, m } = rowData.row.original;
          return (
            <>
              {m && !(docId == 208 && disableEmptyYard) ? <span className="mandatory">*</span> : ''}
              <span title={name || ''}>{name || ''}</span>
            </>
          );
        },
        width: 200,
        maxWidth: 180,
      },
      {
        accessor: 'fileName',
        Header: 'Upload',
        width: 300,
        maxWidth: 350,
        Cell: (rowData: any) => {
          const { docId, fileName, loading, id, doReleasePdfId, serverPath, tempName } =
            rowData.row.original;

          if (isViewOnly) {
            if (releaseInstructionConfig?.fields?.allowMultipleDocumentUpload && pilmzUploadedDocs[docId]?.length) {
              // PIL MZ - New Feature: View and download multiple documents
              return (
                <>
                  {pilmzUploadedDocs[docId].map((doc, index) => (
                    <div key={index}>
                      <span
                        className="fs--1 text-primary cursor-pointer"
                        onClick={() => {
                          if (doc.serverPath) {
                            openDocumentFromUrl(doc.serverPath);
                          }
                        }}
                      >
                        {doc.fileName || `Document ${index + 1}`}
                      </span>
                    </div>
                  ))}
                </>
              );
            }

            return (
              <span
                className="fs--1 text-primary cursor-pointer"
								onClick={async () => {

								  if (doReleasePdfId) {

								    await openDocumentInNewTab({
								      url: '/api/edo-rest-v2/v2/common/openDocument',
								      params: {
								        id: encryptAndEncode(doReleasePdfId),
								        moduleTp: 'DO_RELEASE_PDF',
								        linerId: encryptAndEncode(selectedLinerId),
								      },
								    });

								  } else if (id) {

								    await openDocumentInNewTab({
								      url: '/api/edo-rest-v2/v2/common/openDocument',
								      params: {
								        id: encryptAndEncode(id),
								        moduleTp: 'DND_RELEASE_PDF',
								        linerId: encryptAndEncode(selectedLinerId),
								      },
								    });

								  }

								}}
              >
                {fileName}
              </span>
            );
          }

          if (releaseInstructionConfig?.fields?.allowMultipleDocumentUpload) {
            // PIL MZ - New Feature: Multi document upload
            return (
              <div>
                <UploadThing
                  name={fileName}
                  onOpenDocument={
                    !doReleasePdfId && !id
                      ? undefined
                      : async () => {
                          await openDocumentInNewTab({
                            url: '/api/edo-rest-v2/v2/common/openDocument',
                            params: {
                              id: encryptAndEncode(doReleasePdfId || id),
                              moduleTp: doReleasePdfId ? 'DO_RELEASE_PDF' : 'DND_RELEASE_PDF',
                              linerId: encryptAndEncode(selectedLinerId),
                            },
                          });
                        }
                  }
                  loading={loading}
                  fulfilled={(name: string, path: string, tmpName: string): void => {
                    dispatch(
                      setLinerAttachment({
                        docId,
                        fileName: name,
                        serverPath: path,
                        tempName: tmpName,
                        loading: 100,
                      })
                    );

                    setPilmzUploadedDocs(prev => {
                      const existing = prev[docId] || [];
                      if (existing.length >= 10) {
                        // PIL MZ - New Feature: Multi document upload limit
                        // Using existing toast pattern via UploadThing validations
                        return prev;
                      }
                      return {
                        ...prev,
                        [docId]: [...existing, { fileName: name, serverPath: path, tempName: tmpName }],
                      };
                    });
                  }}
                  failed={(): void => {
                    dispatch(
                      setLinerAttachment({
                        docId,
                        loading: -1,
                        fileName: '',
                      })
                    );
                  }}
                  pending={(progress: number): void => {
                    dispatch(
                      setLinerAttachment({
                        docId,
                        loading: progress,
                      })
                    );
                  }}
                  clear={(): void => {
                    dispatch(
                      setLinerAttachment({
                        doReleasePdfId: null,
                        docId,
                        fileName: '',
                        loading: 0,
                        serverPath: null,
                        tempName: null,
                      })
                    );
                    setPilmzUploadedDocs(prev => ({
                      ...prev,
                      [docId]: [],
                    }));
                  }}
                  selected={(name: string): void => {
                    dispatch(
                      setLinerAttachment({
                        doReleasePdfId: null,
                        docId,
                        fileName: name,
                        loading: 0,
                        serverPath,
                        tempName,
                      })
                    );
                  }}
                  fileLimit={(+attachedFileSize) / 1000000}
                  fileType={['pdf']}
                  labelWidth="250px"
                  disabled={docId == 208 && disableEmptyYard}
                />
                {pilmzUploadedDocs[docId]?.length >= 10 && (
                  <div className="text-danger fs--2">
                    Maximum 10 documents allowed
                  </div>
                )}
              </div>
            );
          }

          return (
            <UploadThing
              name={fileName}
              onOpenDocument={
                !doReleasePdfId && !id
                  ? undefined
                  : async () => {
                      await openDocumentInNewTab({
                        url: '/api/edo-rest-v2/v2/common/openDocument',
                        params: {
                          id: encryptAndEncode(doReleasePdfId || id),
                          moduleTp: doReleasePdfId ? 'DO_RELEASE_PDF' : 'DND_RELEASE_PDF',
                          linerId: encryptAndEncode(selectedLinerId),
                        },
                      });
                    }
              }
              loading={loading}
              fulfilled={(name: string, path: string, tmpName: string): void => {
                dispatch(
                  setLinerAttachment({
                    docId,
                    fileName: name,
                    serverPath: path,
                    tempName: tmpName,
                    loading: 100,
                  })
                );
              }}
              failed={(): void => {
                dispatch(
                  setLinerAttachment({
                    docId,
                    loading: -1,
                    fileName: '',
                  })
                );
              }}
              pending={(progress: number): void => {
                dispatch(
                  setLinerAttachment({
                    docId,
                    loading: progress,
                  })
                );
              }}
              clear={(): void => {
                dispatch(
                  setLinerAttachment({
                    doReleasePdfId: null,
                    docId,
                    fileName: '',
                    loading: 0,
                    serverPath: null,
                    tempName: null,
                  })
                );
              }}
              selected={(name: string): void => {
                dispatch(
                  setLinerAttachment({
                    doReleasePdfId: null,
                    docId,
                    fileName: name,
                    loading: 0,
                    // serverPath,
                    // tempName,
                    serverPath: null,
                    tempName: null
                  })
                );
              }}
              fileLimit={(+attachedFileSize) / 1000000}
              fileType={['pdf']}
              labelWidth="250px"
              disabled={docId == 208 && disableEmptyYard}
            />
          );
        },
      },
      {
        accessor: 'cfsName',
        Header:
          selectedLinerId === '24118'
            ? t('resource_2:cfsIcdNamePIL')
            : t('resource_2:cfsIcdName'),
        width: 200,
        maxWidth: 250,
        Cell: TableCellCfsList,
      },
    ],
    [
      isViewOnly,
      attachedFileSize,
      selectedLinerId,
      selectedCfsName,
      selectedEmptyYardName,
      csfList,
      emptyYardList,
      disableEmptyYard,
      t,
    ]
  );

  const staticHiddenCols = useMemo(
    () =>
      rbac.rbacDetails.doExtCfsEmptyName == 'Y'
        ? ['dndInstructionAttId']
        : ['cfsName', 'dndInstructionAttId'],
    [rbac.rbacDetails.doExtCfsEmptyName]
  );

  const shouldShowTable =
    releaseInstructionConfig?.fields?.showDocumentAttachmentSection &&
    (profileDetails.partnerType == 'liner' ||
      demurrageViewForm.status == 'Completed' ||
      linerDocs.some((data) => data?.isRejected && !!data.filePath)) &&
    linerDocs?.length > 0;

  if (!shouldShowTable) {
    return null;
  }

  return (
    <div className="col-lg-12 mt-2 mb-2 demmurageTable">
      {approveRejectAccess && (
        <span className="badge bg-secondary mb-2">{t('resource_message:docInfo')} </span>
      )}

      <DataTable
        data={linerDocs}
        gridName="LINER_RELEASE_ATTACHMENT_DO_EXTENSION"
        columns={linerDocAttachmentColumns}
        sortable={false}
        searchable={false}
        showColumnFilter={false}
        selection={false}
        perPage={10}
        pagination={false}
        showExport={false}
        uniqueFilterKey="releaseAttachment"
        autoHeight={true}
        staticHiddenCols={staticHiddenCols}
      />
    </div>
  );
};

export default LinerReleaseAttachmentTable;
